import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonComponent, InputComponent, NotificationComponent } from '@zero/shared/ui-web-design-system';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, take } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';
import { PasswordRepeatPipe } from '../../pipes/validation/password-repeat/password-repeat.pipe';
import { PasswordPipe } from '../../pipes/validation/password/password.pipe';

@Component({
  imports: [
    OnboardingContainerComponent,
    ButtonComponent,
    TranslocoPipe,
    InputComponent,
    NotificationComponent,
    PasswordPipe,
    PasswordRepeatPipe,
    AsyncPipe,
  ],
  templateUrl: './password.component.html',
  styleUrl: './password.component.scss',
})
export class PasswordComponent extends BaseComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #password = new BehaviorSubject<string>('');
  readonly #passwordRepeat = new BehaviorSubject<string>('');

  public readonly password$ = this.#password.asObservable();
  public readonly passwordRepeat$ = this.#passwordRepeat.asObservable();
  public readonly isFormValid$ = combineLatest([this.password$, this.passwordRepeat$, this.facade.sendingPassword$]).pipe(
    map(
      ([password, passwordRepeat, sendingPassword]) =>
        !!(new PasswordPipe().transform(password) && new PasswordRepeatPipe().transform(passwordRepeat, password) && !sendingPassword),
    ),
  );

  public handlePasswordChange = (password: string) => this.#password.next(password);
  public handlePasswordRepeatChange = (passwordRepeat: string) => this.#passwordRepeat.next(passwordRepeat);

  protected override async submit(): Promise<void> {
    (await firstValueFrom(this.isFormValid$)) && this.facade.signUp(this.#password.value);
  }

  protected override populateFromData(): void {
    this.facade.email$
      .pipe(
        take(1),
        filter((email) => !email),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToPreviousStep());
  }

  protected override listenToSuccessfulSubmit(): void {
    this.facade.sentPasswordSuccessfully$
      .pipe(
        filter((sent) => !!sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToNextStep());
  }
}
